// src/pages/CityWatch.jsx
// CityWatch marketing landing page (board-watch.com/city-watch). A city-council
// spin-off of BoardWatch: same features (meetings, topics, votings, search),
// city framing. Renders under the shared marketing chrome; BrandContext swaps
// the navbar/footer to CityWatch for any /city-watch route.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { useBrand } from "../brand/BrandContext";
import { useDocumentMeta } from "../brand/useDocumentMeta";
import "./CityWatch.css";

const FEATURES = [
  {
    title: "Meetings",
    body: "Every Board of Supervisors and committee meeting, transcribed and summarized into the agenda items that actually affect residents.",
  },
  {
    title: "Votings",
    body: "Every recorded vote, attributed to the individual supervisor by name — so you can see exactly how each member voted on the budget, housing, and public safety.",
  },
  {
    title: "Topics",
    body: "A glanceable, deduplicated feed of what the city is actually deciding: rezonings, appropriations, ordinances, contracts.",
  },
  {
    title: "Search",
    body: "Ask a question in plain English and get citation-backed answers pulled straight from the meeting record, jump-linked to the moment it was said.",
  },
];

const STEPS = [
  { n: "1", t: "We ingest the record", d: "Meeting video is pulled the moment it is published, transcribed with speaker diarization, and each speaker is matched to the supervisor who spoke." },
  { n: "2", t: "AI structures it", d: "Agenda items are scored by resident impact, budget figures are extracted with their baselines, and every formal vote is captured with per-member attribution." },
  { n: "3", t: "You get clarity", d: "Browse, search, and cite — no more scrubbing hours of video to find the one decision you care about." },
];

function CityWatch() {
  const brand = useBrand();
  useDocumentMeta({ title: brand.title, description: brand.description });

  const [org, setOrg] = useState("");
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!org || !email || !name) {
      alert("Please fill in the required fields (City / Body, Name, Email).");
      return;
    }
    setLoading(true);
    setFeedback(null);
    try {
      const { error } = await supabase.from("inbound_requests").insert([
        {
          organization: org,
          website,
          contact_name: name,
          contact_email: email,
          notes: notes ? `[CityWatch] ${notes}` : "[CityWatch]",
        },
      ]);
      if (error) throw error;
      setFeedback("success");
      setOrg(""); setWebsite(""); setName(""); setEmail(""); setNotes("");
    } catch (err) {
      console.error("[CityWatch] lead submit error:", err);
      setFeedback("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cw">
      {/* Hero */}
      <header className="cw-hero">
        <p className="cw-kicker">About CityWatch</p>
        <h1 className="cw-title">City council meetings, finally easy to follow.</h1>
        <p className="cw-subtitle">
          CityWatch turns your city's public meetings into searchable, citation-backed
          insights — every agenda item, every dollar, and every supervisor's vote,
          without watching hours of video.
        </p>
        <div className="cw-cta-row">
          <Link to={brand.demoPath} className="cw-btn cw-btn--primary">
            See it live: SF Board of Supervisors
          </Link>
          <a href="#cw-request" className="cw-btn cw-btn--ghost">Bring CityWatch to your city</a>
        </div>
      </header>

      {/* Features */}
      <section className="cw-section">
        <h2 className="cw-h2">Everything BoardWatch does, for city hall.</h2>
        <div className="cw-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className="cw-card">
              <h3 className="cw-card__title">{f.title}</h3>
              <p className="cw-card__body">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="cw-section cw-section--muted">
        <h2 className="cw-h2">How it works</h2>
        <div className="cw-steps">
          {STEPS.map((s) => (
            <div key={s.n} className="cw-step">
              <div className="cw-step__num">{s.n}</div>
              <div>
                <h3 className="cw-step__title">{s.t}</h3>
                <p className="cw-step__body">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lead capture */}
      <section id="cw-request" className="cw-section">
        <div className="cw-request">
          <div className="cw-request__copy">
            <h2 className="cw-h2">Bring CityWatch to your city</h2>
            <p className="cw-request__lede">
              We're expanding beyond San Francisco. Tell us which body you want covered
              and we'll be in touch.
            </p>
          </div>
          <form className="cw-form" onSubmit={handleSubmit}>
            <label className="cw-field">
              <span>City / governing body *</span>
              <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="e.g. Oakland City Council" required />
            </label>
            <label className="cw-field">
              <span>Website</span>
              <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" />
            </label>
            <label className="cw-field">
              <span>Your name *</span>
              <input value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label className="cw-field">
              <span>Email *</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label className="cw-field cw-field--full">
              <span>Anything else?</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </label>
            <div className="cw-field--full">
              <button type="submit" className="cw-btn cw-btn--primary" disabled={loading}>
                {loading ? "Sending…" : "Request coverage"}
              </button>
              {feedback === "success" && <span className="cw-note cw-note--ok">Thanks — we'll be in touch.</span>}
              {feedback === "error" && <span className="cw-note cw-note--err">Something went wrong. Email {brand.mailto}.</span>}
            </div>
          </form>
        </div>
      </section>

      <p className="cw-fineprint">
        CityWatch is a {" "}
        <Link to="/">BoardWatch</Link> product. Read our <Link to="/about">mission</Link>.
      </p>
    </div>
  );
}

export default CityWatch;
